/**
 * Модуль для работы с подарками (отправка, получение, просмотр)
 */

import { logger } from "../../utils/logger.js";

/**
 * Экранирует HTML для безопасного отображения
 */
function escapeHtml(text) {
  if (typeof text !== "string") return "";
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Создаёт менеджер для работы с подарками
 */
export function createGiftsScreenManager({
  safeFetch,
  buildAuthHeaders,
  getApiUrl,
  getTelegramWebApp,
  fetchProfessionsData,
  renderProfessionBadge,
  showScreen,
  escapeHtmlFn = escapeHtml,
  documentRef = document,
  initUserAutocomplete = null, // Функция для инициализации автокомплита (fs из legacy-app)
  hideUserAutocomplete = null, // Функция для скрытия автокомплита (he из legacy-app)
  getCurrentUser = null, // Функция для получения текущего пользователя
  setCurrentUser = null, // Функция для установки текущего пользователя
  updateUserBalancesUI = null, // Функция для обновления UI балансов
}) {
  let selectedCreatureId = null;

  /**
   * Показывает экран отправки подарка
   */
  async function showSendGiftScreen() {
    documentRef
      .querySelectorAll(".screen")
      .forEach((el) => el.classList.remove("active"));
    const screen = documentRef.getElementById("send-gift-screen");
    if (screen) {
      screen.classList.add("active");
    }

    selectedCreatureId = null;
    const selectedInfo = documentRef.getElementById("selected-creature-info");
    const recipientEl = documentRef.getElementById("gift-recipient");
    const messageEl = documentRef.getElementById("gift-message");

    if (selectedInfo) {
      selectedInfo.style.display = "none";
    }
    if (recipientEl) {
      recipientEl.style.display = "block";
      recipientEl.value = "";
    }
    if (messageEl) {
      messageEl.value = "";
    }

    // Инициализируем автокомплит пользователей
    if (hideUserAutocomplete) {
      hideUserAutocomplete();
    }
    if (initUserAutocomplete) {
      setTimeout(() => {
        initUserAutocomplete();
      }, 100);
    }

    // Загружаем коллекцию для выбора существа
    const domCache = window.__domCacheManager;
    const loadingManager = window.__loadingIndicatorManager;
    
    const container = domCache?.getCachedElement("#send-gift-creatures", documentRef) 
      || documentRef.getElementById("send-gift-creatures");
    
    if (!container) {
      return;
    }

    // Показываем skeleton loader
    if (loadingManager) {
      loadingManager.showLoading("gifts-load", {
        target: container,
        type: "skeleton"
      });
    } else {
      const loadingHTML = '<div class="loading">Загрузка коллекции...</div>';
      domCache?.updateHTML("#send-gift-creatures", loadingHTML, documentRef) ||
      (container.innerHTML = loadingHTML);
    }

    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/user/me`, {
        headers: buildAuthHeaders(),
      });
      const data = await response.json();

      if (data.success && data.collection) {
        container.innerHTML = "";

        if (data.collection.length === 0) {
          const emptyHTML = '<div class="loading">У вас нет существ для отправки</div>';
          domCache?.updateHTML("#send-gift-creatures", emptyHTML, documentRef) ||
          (container.innerHTML = emptyHTML);
          if (loadingManager) loadingManager.hideLoading("gifts-load");
          return;
        }

        try {
          await fetchProfessionsData();
        } catch (error) {
          console.warn(
            "Не удалось получить данные профессий для экрана подарков:",
            error?.message || error,
          );
        }

        data.collection.forEach((creature) => {
          const card = renderGiftCreatureCard(creature);
          if (card) {
            container.appendChild(card);
          }
        });
      }
    } catch (error) {
      logger.error("Load collection for gift error:", error);
      const errorHTML = '<div class="error">Ошибка загрузки коллекции</div>';
      domCache?.updateHTML("#send-gift-creatures", errorHTML, documentRef) ||
      (container.innerHTML = errorHTML);
      if (loadingManager) loadingManager.hideLoading("gifts-load");
    }
  }

  /**
   * Рендерит карточку существа для выбора подарка
   */
  function renderGiftCreatureCard(creature) {
    const card = documentRef.createElement("div");
    card.className = "creature-card";
    card.dataset.creatureId = creature.creatureId || creature.id;

    const rarity = (creature.rarity || "").toLowerCase() || "common";
    const rarityLabels = {
      common: "Обычный",
      rare: "Редкий",
      legendary: "Легендарный",
    };
    const name = escapeHtmlFn(creature.name || creature.creature_name || "");
    const imageUrl =
      escapeHtmlFn(creature.imageUrl || creature.creature_image || "/creatures/1.png");
    const level = creature.level || 0;
    const exp = creature.exp || 0;
    const profession = creature.profession || creature.profession_id || null;
    const professionLevel = creature.profession_level || creature.professionLevel || 0;
    const professionBadge = profession
      ? renderProfessionBadge(profession, professionLevel, { benefitsCount: 1 })
      : "";

    card.innerHTML = `
      <div class="creature-img"><img src="${imageUrl}" style="width: 100%; height: 100%; object-fit: contain;" onerror="this.parentElement.textContent='🐾'; this.remove();"></div>
      <div class="creature-name">${name}</div>
      <div style="font-size: 12px; color: var(--tg-theme-hint-color);">Уровень ${level} (${exp}/100)</div>
      <div class="rarity-badge rarity-${rarity}">${escapeHtmlFn(rarityLabels[rarity] || rarity)}</div>
      ${professionBadge}
    `;

    card.onclick = () => selectCreatureForGift(creature.creatureId || creature.id);
    return card;
  }

  /**
   * Выбирает существо для подарка
   */
  function selectCreatureForGift(creatureId) {
    selectedCreatureId = creatureId;

    documentRef
      .querySelectorAll("#send-gift-creatures .creature-card")
      .forEach((card) => {
        card.style.border = "none";
        if (card.dataset.creatureId == creatureId) {
          card.style.border = "3px solid var(--tg-theme-link-color)";
        }
      });

    const selectedInfo = documentRef.getElementById("selected-creature-info");
    if (selectedInfo) {
      const cards = Array.from(
        documentRef.querySelectorAll("#send-gift-creatures .creature-card"),
      );
      const selectedCard = cards.find(
        (card) => card.dataset.creatureId == creatureId,
      );
      if (selectedCard) {
        const nameEl = selectedCard.querySelector(".creature-name");
        if (nameEl) {
          selectedInfo.innerHTML = `✅ Выбрано: ${nameEl.textContent}`;
          selectedInfo.style.display = "block";
        }
      }
    }
  }

  /**
   * Отправляет подарок
   */
  async function sendGift() {
    const telegramWebApp = getTelegramWebApp();
    if (!selectedCreatureId) {
      if (telegramWebApp?.showAlert) {
        telegramWebApp.showAlert("Выберите существо для подарка");
      }
      return;
    }

    const recipientEl = documentRef.getElementById("gift-recipient");
    if (!recipientEl) {
      return;
    }

    const recipient = recipientEl.value.trim();
    if (!recipient) {
      if (telegramWebApp?.showAlert) {
        telegramWebApp.showAlert("Введите @username получателя");
      }
      return;
    }

    const username = recipient.startsWith("@") ? recipient.substring(1) : recipient;
    if (!username) {
      if (telegramWebApp?.showAlert) {
        telegramWebApp.showAlert("Введите корректный @username");
      }
      return;
    }

    const messageEl = documentRef.getElementById("gift-message");
    const message = messageEl ? messageEl.value.trim() || null : null;

    const button = documentRef.querySelector('button[onclick*="sendGift"]');
    const originalText = button ? button.textContent : "";
    if (button) {
      button.disabled = true;
      button.textContent = "⏳ Отправка...";
    }

    try {
      const apiUrl = getApiUrl();
      const response = await safeFetch(`${apiUrl}/gifts/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...buildAuthHeaders(),
        },
        body: JSON.stringify({
          creatureId: selectedCreatureId,
          toUsername: username,
          message: message,
        }),
      });

      if (!response.success) {
        throw new Error(response.error || "Ошибка отправки подарка");
      }

      const data = response.data;
      if (data.success) {
        // Обновляем баланс пользователя после списания звёзд за передаривание
        if (data.userData && getCurrentUser && setCurrentUser && updateUserBalancesUI) {
          const currentUser = getCurrentUser();
          if (currentUser) {
            const updatedUser = { ...currentUser, ...data.userData };
            setCurrentUser(updatedUser);
            updateUserBalancesUI(updatedUser);
          }
        }
        
        let message = "Подарок отправлен! 🎁";
        if (data.regiftCost && data.regiftCost > 0) {
          message = data.message || `Подарок отправлен! Списано ${data.regiftCost} ⭐ за передаривание`;
        }
        if (telegramWebApp?.showAlert) {
          telegramWebApp.showAlert(message);
        }
        selectedCreatureId = null;
        if (recipientEl) {
          recipientEl.value = "";
        }
        if (messageEl) {
          messageEl.value = "";
        }
        showScreen("gifts");
        loadSentGifts();
      } else {
        const errorMessage =
          data.error === "creature_not_in_collection"
            ? "Существо не найдено в коллекции"
            : data.error === "user_not_found"
              ? "Пользователь не найден. Убедитесь, что пользователь зарегистрирован в приложении."
              : data.error === "cannot_send_to_yourself"
                ? "Нельзя отправить подарок самому себе"
              : data.error === "not_enough_stars"
                ? data.message || `Недостаточно звёзд. Требуется: ${data.required || 0}, у вас: ${data.current || 0}`
              : data.error === "regift_cooldown"
                ? data.message || "Нельзя передарить существо обратно тому же пользователю в течение 24 часов после получения"
                : data.message || "Ошибка отправки подарка";
        if (telegramWebApp?.showAlert) {
          telegramWebApp.showAlert(errorMessage);
        }
      }
    } catch (error) {
      console.error("Send gift error:", error);
      if (telegramWebApp?.showAlert) {
        telegramWebApp.showAlert(
          "Ошибка при отправке подарка: " + (error.message || "Неизвестная ошибка"),
        );
      }
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = originalText;
      }
    }
  }

  /**
   * Загружает входящие подарки
   */
  async function loadIncomingGifts() {
    const domCache = window.__domCacheManager;
    const loadingManager = window.__loadingIndicatorManager;
    
    const container = domCache?.getCachedElement("#gifts-list", documentRef) 
      || documentRef.getElementById("gifts-list");
    
    if (!container) {
      return;
    }

    // Показываем skeleton loader
    if (loadingManager) {
      loadingManager.showLoading("gifts-incoming-load", {
        target: container,
        type: "skeleton"
      });
    } else {
      const loadingHTML = '<div class="loading">Загрузка...</div>';
      domCache?.updateHTML("#gifts-list", loadingHTML, documentRef) ||
      (container.innerHTML = loadingHTML);
    }

    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/gifts/inbox`, {
        headers: buildAuthHeaders(),
      });
      const data = await response.json();

      if (data.success) {
        container.innerHTML = "";

        if (data.gifts.length === 0) {
          const emptyHTML = '<div class="loading">Нет входящих подарков</div>';
          domCache?.updateHTML("#gifts-list", emptyHTML, documentRef) ||
          (container.innerHTML = emptyHTML);
          if (loadingManager) loadingManager.hideLoading("gifts-incoming-load");
          return;
        }

        data.gifts.forEach((gift) => {
          const item = renderGiftItem(gift, true);
          if (item) {
            container.appendChild(item);
          }
        });
      }
    } catch (error) {
      logger.error("Load incoming gifts error:", error);
      const errorHTML = '<div class="error">Ошибка загрузки подарков</div>';
      domCache?.updateHTML("#gifts-list", errorHTML, documentRef) ||
      (container.innerHTML = errorHTML);
      if (loadingManager) loadingManager.hideLoading("gifts-incoming-load");
    } finally {
      if (loadingManager) loadingManager.hideLoading("gifts-incoming-load");
    }
  }

  /**
   * Загружает отправленные подарки
   */
  async function loadSentGifts() {
    const domCache = window.__domCacheManager;
    const loadingManager = window.__loadingIndicatorManager;
    
    const container = domCache?.getCachedElement("#gifts-list", documentRef) 
      || documentRef.getElementById("gifts-list");
    
    if (!container) {
      return;
    }

    // Показываем skeleton loader
    if (loadingManager) {
      loadingManager.showLoading("gifts-sent-load", {
        target: container,
        type: "skeleton"
      });
    } else {
      const loadingHTML = '<div class="loading">Загрузка...</div>';
      domCache?.updateHTML("#gifts-list", loadingHTML, documentRef) ||
      (container.innerHTML = loadingHTML);
    }

    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/gifts/sent`, {
        headers: buildAuthHeaders(),
      });
      const data = await response.json();

      if (data.success) {
        container.innerHTML = "";

        if (data.gifts.length === 0) {
          const emptyHTML = '<div class="loading">Нет отправленных подарков</div>';
          domCache?.updateHTML("#gifts-list", emptyHTML, documentRef) ||
          (container.innerHTML = emptyHTML);
          if (loadingManager) loadingManager.hideLoading("gifts-sent-load");
          return;
        }

        data.gifts.forEach((gift) => {
          const item = renderGiftItem(gift, false);
          if (item) {
            container.appendChild(item);
          }
        });
      }
    } catch (error) {
      logger.error("Load sent gifts error:", error);
      const errorHTML = '<div class="error">Ошибка загрузки подарков</div>';
      domCache?.updateHTML("#gifts-list", errorHTML, documentRef) ||
      (container.innerHTML = errorHTML);
      if (loadingManager) loadingManager.hideLoading("gifts-sent-load");
    } finally {
      if (loadingManager) loadingManager.hideLoading("gifts-sent-load");
    }
  }

  /**
   * Рендерит элемент подарка в списке
   */
  function renderGiftItem(gift, isIncoming) {
    const item = documentRef.createElement("div");
    item.className = "gift-item";

    const fromUsername = escapeHtmlFn(gift.from_username || "");
    const toUsername = escapeHtmlFn(gift.to_username || "");
    const creatureName = escapeHtmlFn(gift.creature_name || "Существо");
    const message = gift.message ? escapeHtmlFn(gift.message) : null;
    const status = gift.status || "pending";
    const createdAt = gift.created_at || "";
    const giftId = gift.id;

    let statusText = "";
    if (status === "accepted") {
      statusText = "✅ Принят";
    } else if (status === "declined") {
      statusText = "❌ Отклонён";
    } else {
      statusText = "⏳ Ожидает";
    }

    // Форматируем дату для отображения
    let formattedDate = createdAt;
    try {
      const date = new Date(createdAt);
      if (!isNaN(date.getTime())) {
        formattedDate = date.toLocaleString("ru-RU", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
    } catch (e) {
      // Используем исходную дату при ошибке
    }

    // Кнопки для входящих подарков со статусом pending
    let actionButtons = "";
    if (isIncoming && status === "pending") {
      actionButtons = `
        <div style="display: flex; gap: 8px; margin-top: 12px;">
          <button class="btn" onclick="window.acceptGift(${giftId})" style="flex: 1;">✅ Принять</button>
          <button class="btn btn-secondary" onclick="window.declineGift(${giftId})" style="flex: 1;">❌ Отклонить</button>
        </div>
      `;
    }

    item.innerHTML = `
      <div style="font-weight: 600; font-size: 16px;">
        ${isIncoming ? `От: @${fromUsername}` : `Кому: @${toUsername}`}
      </div>
      <div style="font-size: 14px; color: var(--tg-theme-hint-color); margin-top: 4px;">
        ${creatureName}
      </div>
      ${message ? `<div style="font-size: 13px; margin-top: 6px;">${message}</div>` : ""}
      <div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-top: 8px;">
        ${statusText} • ${formattedDate}
      </div>
      ${actionButtons}
    `;

    return item;
  }

  /**
   * Принимает подарок
   */
  async function acceptGift(giftId) {
    const telegramWebApp = getTelegramWebApp();
    try {
      const apiUrl = getApiUrl();
      const response = await safeFetch(`${apiUrl}/gifts/${giftId}/accept`, {
        method: "POST",
        headers: buildAuthHeaders(),
      });

      if (!response.success) {
        throw new Error(response.error || "Ошибка принятия подарка");
      }

      const data = response.data;
      if (data.success) {
        // Обновляем баланс пользователя после принятия подарка
        if (data.userData && getCurrentUser && setCurrentUser && updateUserBalancesUI) {
          const currentUser = getCurrentUser();
          if (currentUser) {
            const updatedUser = { ...currentUser, ...data.userData };
            setCurrentUser(updatedUser);
            updateUserBalancesUI(updatedUser);
          }
        }
        
        let message = "Подарок принят! 🎁";
        if (data.royalty) {
          message = `Подарок принят! Отправитель получил ${data.royalty.coins} монет роялти 🎁`;
          if (data.professionBonus) {
            message += `\n\n✨ Бонус профессии Искажатель изображения: +${data.professionBonus.bonus} монет (${data.professionBonus.bonusPercent}%)`;
          }
        }

        if (telegramWebApp?.showAlert) {
          telegramWebApp.showAlert(message);
        }

        // Обновляем список входящих подарков
        loadIncomingGifts();
      }
    } catch (error) {
      console.error("Accept gift error:", error);
      if (telegramWebApp?.showAlert) {
        telegramWebApp.showAlert(
          "Ошибка при принятии подарка: " +
            (error.message || "Неизвестная ошибка"),
        );
      }
    }
  }

  /**
   * Отклоняет подарок
   */
  async function declineGift(giftId) {
    const telegramWebApp = getTelegramWebApp();
    try {
      const apiUrl = getApiUrl();
      const response = await safeFetch(`${apiUrl}/gifts/${giftId}/decline`, {
        method: "POST",
        headers: buildAuthHeaders(),
      });

      if (!response.success) {
        throw new Error(response.error || "Ошибка отклонения подарка");
      }

      if (response.data.success) {
        if (telegramWebApp?.showAlert) {
          telegramWebApp.showAlert("Подарок отклонён");
        }

        // Обновляем список входящих подарков
        loadIncomingGifts();
      }
    } catch (error) {
      console.error("Decline gift error:", error);
      if (telegramWebApp?.showAlert) {
        telegramWebApp.showAlert(
          "Ошибка при отклонении подарка: " +
            (error.message || "Неизвестная ошибка"),
        );
      }
    }
  }

  return {
    showSendGiftScreen,
    sendGift,
    loadIncomingGifts,
    loadSentGifts,
    selectCreatureForGift,
    acceptGift,
    declineGift,
  };
}

